import { createSelector } from "reselect"
import { get, chain, size, some, isEmpty, differenceWith, pick, sumBy, map, keyBy } from "lodash"
import { percentageFormatter } from "helpers/formatters/numbers"
import { formatLabel, positionsComparator, getTickerAndMicFromContributor } from "helpers/securities"
import { NORMALIZATION } from "helpers/segments"
import { getPositionAction } from "./index"
import { formatItemLabel, getInstrumentCompositionPositions, getSmartTradePositionSet } from "./utils"
import { buildContributorKey } from "helpers/contributors"

const EMPTY_ARRAY = []

export const getRemovedSecurities = state => get(state, "modules.smartTradePanel.removedSecurities")
const getInstrumentDescriptors = (_, props) => get(props, "instrumentDescriptors")
export const getInstrumentPortfolio = (_, props) => get(props, "instrumentPortfolio")
const getRawInstrumentPortfolioPositions = (_, props) => get(props, "instrumentPortfolioPositions")
const getSelectExperimentPositionSet = (state) => get(state, "global.data.selectedExperimentPositionSet.data.researchTopic.experiment")
const getRawSmartTradeComposition = (state) => get(state, "global.data.smartPortfolioPositions.data.model.simulation.composition.0.positions", EMPTY_ARRAY)
const getRawSmartTradeDollarComposition = state => get(state, "global.data.smartPortfolioPositions.data.model.simulation.dollarComposition.0.positions", EMPTY_ARRAY)

const getBasketSegment = (_, props) => {
  const segment = get(props, "segment")
  if (!segment) { return null }
  return {
    ...segment,
    normalization: NORMALIZATION.STANDALONE, // Segments in Smart Trade Panel are always standalone
  }
}

export const getRawSmartTradePositions = createSelector(
  [getRawSmartTradeComposition, getRawSmartTradeDollarComposition],
  (positions, dollarPositions) => {
    const mappedDollarPositions = keyBy(dollarPositions, buildContributorKey)
    return map(positions, position => ({
      ...position,
      id: {
        // we keep the composition id for comparison purposes
        id: position.id,
        ...getTickerAndMicFromContributor(position),
        ...pick(position, ["internalId", "modelProviderId"]),
      },
      dollarValue: get(mappedDollarPositions, [buildContributorKey(position), "economicExposure"]),
    }))
  },
)

// Wanna leave this here in case we have to implement a toggle on the toggle on the "Normalization"
// getBasketPositionsAsContributors
// const getBasketPositions = (state) => get(state, 'global.data.smartTradePanelBasket.data.model.simulation.basketSummary.0.positions', [])
export const getBasketPositions = createSelector(
  [getInstrumentCompositionPositions, getRawSmartTradePositions],
  (instrumentPositions, rawSmartTradePositions) => differenceWith(rawSmartTradePositions, instrumentPositions, positionsComparator),
)

export const getBasketPostionsGMV = createSelector(
  [getBasketPositions],
  (basketPositions) => sumBy(basketPositions, pos => Math.abs(pos.dollarValue)),
)

export const getInstrumentSize = createSelector(
  [getInstrumentCompositionPositions, getRawSmartTradePositions],
  (instrumentPositions, rawSmartTradePositions) => chain(rawSmartTradePositions)
    .intersectionWith(instrumentPositions, positionsComparator)
    .reduce((acc, value) => ({
      dollarValue: Math.abs(acc.dollarValue) + Math.abs(value.dollarValue),
      economicExposure: Math.abs(acc.economicExposure) + Math.abs(value.economicExposure),
    }), { dollarValue: 0, economicExposure: 0 })
    .value(),
)

export const getInstrumentSecurityPositions = createSelector(
  [getInstrumentDescriptors, getInstrumentPortfolio],
  (instrument, instrumentPortfolio) => !isEmpty(instrumentPortfolio)
    ? null : [{ value: percentageFormatter(100, true), label: formatLabel(instrument), ...instrument }],
)

export const getInstrumentPortfolioPositions = createSelector(
  [getRawInstrumentPortfolioPositions],
  (portfolioPositions) => chain(portfolioPositions)
    .omit(["gmv", "equity"])
    .map(positions => positions)
    .flatten()
    .compact()
    .value(),
)

export const getRefInstrumentPositions = createSelector(
  [getInstrumentSecurityPositions, getInstrumentPortfolioPositions],
  (securityPositions, portfolioPositions) => securityPositions || portfolioPositions,
)

export const getApplyEnabled = createSelector(
  [getRemovedSecurities],
  (removedSecurities) => size(removedSecurities) > 0,
)

/**
 * Returns the "trades" displayed in the "Smart Trade" panel by comparing experiment positions with the instrument portfolio.
 * Any positions that are not in the instrument are considered to be new trades.
 * We only return equities and indexes because these are the only asset types that will be included in a smart trade "basket".
 */
export const getSmartTradeBasketPositionSet = createSelector(
  [getInstrumentPortfolioPositions, getInstrumentDescriptors, getSelectExperimentPositionSet, getSmartTradePositionSet, getBasketSegment],
  (instrumentPortfolioPositions, instrumentDescriptors, selectedExperimentPositionSet, smartTradePositionSet, basketSegment) => {
    const basketPositionSet = selectedExperimentPositionSet || smartTradePositionSet

    let instrumentPositions = instrumentPortfolioPositions
    if (isEmpty(instrumentPositions)) {
      instrumentPositions = [{ id: pick(instrumentDescriptors, ["ticker", "mic", "internalId", "modelProviderId"]) }]
    }

    const firstBasketDate = get(basketPositionSet, "dates.0")
    const equities = !basketSegment ? differenceWith(get(firstBasketDate, "equities"), instrumentPositions, positionsComparator) : get(firstBasketDate, "equities")
    const indexes = !basketSegment ? differenceWith(get(firstBasketDate, "indexes"), instrumentPositions, positionsComparator) : get(firstBasketDate, "indexes")

    if (isEmpty(equities) && isEmpty(indexes)) {
      return null
    }

    return {
      ...basketPositionSet,
      ...(basketSegment && { segment: basketSegment }),
      dates: [
        {
          date: get(basketPositionSet, "dates.0.date"),
          equities,
          indexes,
        },
      ],
    }
  },
)

const getSmartSecurityBasketSecurities = createSelector(
  [getBasketPositions, getRemovedSecurities],
  (positions, removedSecurities) => chain(positions)
    .orderBy(elem => Math.abs(elem.economicExposure), "desc")
    .filter(elem => !some(removedSecurities, { id: elem.id }))
    .map(elem => ({
      economicExposure: elem.economicExposure,
      dollarValue: elem.dollarValue,
      value: percentageFormatter(elem.economicExposure, true),
      id: elem.id,
      label: formatItemLabel(elem),
      assetClass: elem.assetClass,
      action: getPositionAction(elem),
    }))
    .value(),
)

const getSmartPortfolioBasketSecurities = createSelector(
  [getBasketPositions, getRemovedSecurities, getBasketPostionsGMV],
  (positions, removedSecurities, basketPostionsGMV) => chain(positions)
    .orderBy(elem => Math.abs(elem.economicExposure), "desc")
    .filter(elem => !some(removedSecurities, { id: elem.id }))
    .map(elem => ({
      id: elem.id,
      economicExposure: elem.economicExposure,
      dollarValue: elem.dollarValue,
      value: percentageFormatter(elem.dollarValue / basketPostionsGMV * 100, true),
      label: formatItemLabel(elem),
      assetClass: elem.assetClass,
      action: getPositionAction(elem),
    }))
    .value(),
)

// Delegates to the memoized basket selector for the active mode only, rather than computing
// both — the inactive branch reads mode-specific position shapes that aren't present otherwise.
export const getBasketSecurities = (state, props) => isEmpty(getInstrumentPortfolio(state, props))
  ? getSmartSecurityBasketSecurities(state, props)
  : getSmartPortfolioBasketSecurities(state, props)
